import { memo, useMemo } from 'react';

import { type JustifiedBox } from '@air/component-gallery-view';
import { type Board } from '@air/types';

import BoardThumbnail, { type BoardThumbnailProps } from '~/components/BoardThumbnail/BoardThumbnail';
import { getPreferenceImgixFit, useThumbnailPreference } from '~/hooks/useThumbnailPreference';

export type GalleryBoardThumbnailProps = Pick<BoardThumbnailProps, 'board' | 'sizes'> & {
  board: Pick<Board, 'id'>;
  box: Pick<JustifiedBox, 'height' | 'width'>;
  onFallbackChange?: (isFallback: boolean) => void;
};

export const GalleryBoardThumbnail = memo(({ board, box, sizes, onFallbackChange }: GalleryBoardThumbnailProps) => {
  const { getPreference } = useThumbnailPreference();

  const thumbnailPreference = useMemo(() => getPreference(board.id), [board.id, getPreference]);

  return (
    <div className="pointer-events-none absolute inset-1 overflow-hidden rounded-8">
      <div className="absolute inset-0 z-10 hidden size-full bg-black/10 group-data-[valid-thumbnail=true]/gallery-selectable-card:block" />
      <BoardThumbnail
        board={board}
        height="100%"
        imgixParams={{
          fit: getPreferenceImgixFit(thumbnailPreference),
          h: box.height,
          w: box.width,
        }}
        imgixHeight={box.height}
        imgixWidth={box.width}
        objectFit={thumbnailPreference === 'default' ? 'cover' : 'contain'}
        sizes={sizes}
        width="100%"
        onFallbackChange={onFallbackChange}
      />
    </div>
  );
});

GalleryBoardThumbnail.displayName = 'GalleryBoardThumbnail';
